import { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import MobileEmergencyBar from '../../components/MobileEmergencyBar';
import ContactHero from './components/ContactHero';
import EmergencyContactCard from './components/EmergencyContactCard';
import ContactMethodsGrid from './components/ContactMethodsGrid';
import CallbackForm from './components/CallbackForm';
import ServiceAreaMap from './components/ServiceAreaMap';
import BusinessHours from './components/BusinessHours';
import WhyChooseUs from './components/WhyChooseUs';
import FAQSection from './components/FAQSection';

const Contact = () => {
  const callbackFormRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleEmergencyClick = () => {
    window.location.href = 'tel:0827935785';
  };

  const handleScheduleClick = () => {
    callbackFormRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - meriodine | 24/7 Emergency Service</title>
        <meta
          name="description"
          content="Contact meriodine for immediate plumbing and electrical assistance. Call 082 793 5785 for 24/7 emergency service. 30+ years of trusted expertise."
        />
        <meta
          name="keywords"
          content="contact plumber, emergency plumber, electrician contact, 24/7 service, plumbing emergency, electrical emergency"
        />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <Header />

        <main className="flex-1 pt-16">
          <ContactHero
            onEmergencyClick={handleEmergencyClick}
            onScheduleClick={handleScheduleClick}
          />

          <section className="py-8 md:py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
                <div className="lg:col-span-1">
                  <EmergencyContactCard />
                </div>

                <div className="lg:col-span-2">
                  <ContactMethodsGrid />
                </div>
              </div>
            </div>
          </section>

          <section ref={callbackFormRef} className="py-8 md:py-12 lg:py-16 bg-muted">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                <CallbackForm />
                <ServiceAreaMap />
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                <BusinessHours />
                <FAQSection />
              </div>
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16 bg-muted">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <WhyChooseUs />
            </div>
          </section>

          <section className="py-8 md:py-12 lg:py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
              <div className="bg-gradient-to-r from-accent to-accent/80 text-accent-foreground rounded-lg p-8 md:p-12 text-center shadow-elevated">
                <h2 className="font-headline font-bold text-2xl md:text-3xl lg:text-4xl mb-4">
                  Ready to Get Started?
                </h2>
                <p className="font-body text-base md:text-lg lg:text-xl mb-6 md:mb-8 opacity-90 max-w-2xl mx-auto">
                  Whether it's an emergency or a planned project, we're here to help. Contact us now for expert service.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <a
                    href="tel:0827935785"
                    className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-accent-foreground text-accent rounded-lg font-headline font-semibold text-base md:text-lg hover:opacity-90 transition-opacity emergency-cta w-full sm:w-auto"
                  >
                    <span>📞</span>
                    <span>Call 082 793 5785</span>
                  </a>
                  <button
                    onClick={handleScheduleClick}
                    className="inline-flex items-center justify-center gap-2 px-6 md:px-8 py-3 md:py-4 bg-transparent border-2 border-accent-foreground text-accent-foreground rounded-lg font-headline font-semibold text-base md:text-lg hover:bg-accent-foreground hover:text-accent transition-all w-full sm:w-auto"
                  >
                    <span>📅</span>
                    <span>Request Callback</span>
                  </button>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
        <MobileEmergencyBar />
      </div>
    </>
  );
};

export default Contact;